import { Box, Dialog, IconButton, Typography } from "@mui/material";
import PillButton from "../PillButton";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PetCoverLogo from "@/../public/img/petcover/logo.png";
import PetCoverReviews from "@/../public/img/petcover/reviews-grey.png";
import Image from "next/image";
import { InsuranceLeadForm } from "./InsuranceLeadForm";
import { AdInsuranceModel } from "@/models/AdInsuranceModel";
import Link from "next/link";
import CheckIcon from "@mui/icons-material/Check";
import { InsuranceCaseSource } from "@/case/models/CreateInsuranceCaseViewModel";
import { RecaptchaProvider } from "@/app/layout/useRecaptcha";

function InsuranceUSP({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      <CheckIcon /> {children}
    </Typography>
  );
}

export function InsuranceLeadModal({
  insurance,
  source,
  primary,
}: {
  insurance: AdInsuranceModel;
  source: InsuranceCaseSource;
  primary?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <PillButton
        sx={{ backgroundColor: primary ? "primary.main" : "grey.300" }}
        compact={!primary}
        primary={primary}
        onClick={() => setIsOpen(true)}
      >
        Get a Quote
      </PillButton>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-container": {
            overflow: "auto",
            ".MuiDialog-paper": {
              overflow: "visible",
            },
          },
        }}
      >
        <Box
          sx={(theme) => ({
            backgroundColor: "grey.200",
            padding: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderRadius: theme.spacing(1, 1, 0, 0),
            flexWrap: "wrap",
          })}
        >
          <Box>
            <Typography
              variant="body1"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Equine Insurance
            </Typography>
            <Typography variant="h5">Get a Quote</Typography>
          </Box>
          <Box
            component={Link}
            href="/insurance"
            target="_blank"
            sx={{
              marginLeft: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Image src={PetCoverLogo} alt="Pet Cover Logo" height={45} />
            <Typography variant="caption">
              Horsezone Insurance Partner
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setIsOpen(false)}
            sx={{
              backgroundColor: "white !important",
              position: "absolute",
              top: -24,
              right: -24,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={(theme) => ({
            padding: theme.spacing(4, 2),
            background: "white",
          })}
        >
          <RecaptchaProvider>
            <InsuranceLeadForm insurance={insurance} source={source} />
          </RecaptchaProvider>
        </Box>

        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            backgroundColor: "grey.200",
            padding: theme.spacing(4, 2),
            gap: 4,
            borderRadius: theme.spacing(0, 0, 1, 1),
          })}
        >
          <Box
            sx={{
              textAlign: "center",
            }}
          >
            <Typography variant="h4">Why choose Petcover?</Typography>
            <Typography>
              Australia&apos;s award-winning equine insurance
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: 4,
              [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
                alignItems: "center",
              },
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <InsuranceUSP>
                Up to 100% of eligible vet bills covered for injury & illness
              </InsuranceUSP>
              <InsuranceUSP>Personal Injury add-on for riders</InsuranceUSP>
              <InsuranceUSP>
                Cover for saddlery & tack, horse floats and horse-drawn vehicles
              </InsuranceUSP>
              <InsuranceUSP>
                Cover for foals from 30 days old through to 29-year-old veterans
              </InsuranceUSP>
              <InsuranceUSP>Flexible plans to suit your budget</InsuranceUSP>
            </Box>
            <Link
              href="https://www.feefo.com/en-GB/reviews/petcovergroup"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={PetCoverReviews}
                alt="Pet Cover Reviews"
                height={120}
              />
            </Link>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                textAlign: "center",
              }}
            >
              Protect your horse, protect your ride
            </Typography>
            <Typography>
              Whether you own a horse or lease a pony, have a Shetland or a
              Shire, compete fiercely or happily hack – we&apos;ve got you
              covered.
            </Typography>
            <Typography>
              Find horse insurance to suit your needs and budget – choose from a
              variety of plans, cover levels and optional extras for horses aged
              30 days to 29 years old.
            </Typography>
            <Typography>
              Or select a standalone public liability, saddlery and tack or
              horse trailer and horse-drawn vehicle policy.
            </Typography>
          </Box>
          <Box
            component={Link}
            sx={{
              alignSelf: "center",
            }}
            href="/insurance"
            target="_blank"
          >
            <PillButton>Learn More</PillButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
